import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import packageJson from '../../../../package.json';

// Tipo local para evitar import estático de aws-rum-web en el bundle inicial
interface AwsRumInstance {
  recordPageView(pageId: string | { pageId: string; pageTags?: string[] }): void;
  recordError(error: Error): void;
  recordEvent(eventType: string, eventData: Record<string, unknown>): void;
  addSessionAttributes(attributes: Record<string, string>): void;
}

@Injectable({
  providedIn: 'root'
})
export class RumInitializerService {
  private awsRum: AwsRumInstance | null = null;
  private initPromise: Promise<void> | null = null;

  initializeRum(): void {
    this.initPromise = this.lazyInit();
  }

  private async lazyInit(): Promise<void> {
    const rumConfig = environment.rum;

    if (!rumConfig?.applicationId) {
      return;
    }

    try {
      const { AwsRum } = await import('aws-rum-web');

      this.awsRum = new AwsRum(
        rumConfig.applicationId,
        packageJson.version,
        rumConfig.region,
        {
          sessionSampleRate: rumConfig.sessionSampleRate,
          identityPoolId: rumConfig.identityPoolId,
          endpoint: `https://dataplane.rum.${rumConfig.region}.amazonaws.com`,
          telemetries: [
            'errors',
            [
              'performance',
              {
                eventLimit: 20,
                reportAllLCP: true,
                reportAllCLS: true,
                reportAllINP: true
              }
            ],
            [
              'http',
              {
                recordAllRequests: true,
                addXRayTraceIdHeader: [/api-crud\.aquaforms\.cl/]
              }
            ],
            [
              'interaction',
              {
                events: [{ event: 'click', element: document }],
                enableMutationObserver: false
              }
            ]
          ],
          allowCookies: true,
          enableXRay: rumConfig.enableXRay,
          sessionEventLimit: 0,
          sessionAttributes: {
            appVersion: packageJson.version,
            ambiente: environment.ambiente
          }
        }
      );
    } catch {
      // RUM initialization failed silently
    }
  }

  recordPageView(pageId: string, pageTags?: string[]): void {
    try {
      if (pageTags?.length) {
        this.awsRum?.recordPageView({ pageId, pageTags });
      } else {
        this.awsRum?.recordPageView(pageId);
      }
    } catch {
      // Silently ignore RUM errors
    }
  }

  recordError(error: unknown): void {
    try {
      if (error instanceof Error) {
        this.awsRum?.recordError(error);
      } else {
        this.awsRum?.recordError(new Error(this.extractErrorMessage(error)));
      }
    } catch {
      // Silently ignore RUM errors
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (error == null) return 'Unknown error';
    if (typeof error === 'string') return error;

    const err = error as Record<string, unknown>;

    // HttpErrorResponse: { status, statusText, url, error, message }
    if (err['status'] && err['url']) {
      const status = err['status'];
      const statusText = err['statusText'] || '';
      const url = err['url'];
      const body = err['error'];
      const bodyMsg = typeof body === 'string'
        ? body
        : (body as Record<string, unknown>)?.['message'] || (body as Record<string, unknown>)?.['title'] || '';
      return `HTTP ${status} ${statusText} - ${url}${bodyMsg ? ` | ${bodyMsg}` : ''}`;
    }

    // Objeto con .message
    if (typeof err['message'] === 'string') return err['message'];

    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  recordEvent(eventType: string, eventData: Record<string, unknown>): void {
    try {
      this.awsRum?.recordEvent(eventType, eventData);
    } catch {
      // Silently ignore RUM errors
    }
  }

  setUserContext(userId: string, email?: string, role?: string): void {
    try {
      const attributes: Record<string, string> = { userId };
      if (email) attributes['userEmail'] = email;
      if (role) attributes['userRole'] = role;
      this.awsRum?.addSessionAttributes(attributes);
    } catch {
      // Silently ignore RUM errors
    }
  }

  clearUserContext(): void {
    try {
      this.awsRum?.addSessionAttributes({
        userId: 'anonymous',
        userEmail: '',
        userRole: ''
      });
    } catch {
      // Silently ignore RUM errors
    }
  }
}
